import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as serviceWorker from './serviceWorker';
import { Router } from 'react-router-dom';
import { ApolloProvider } from 'react-apollo';
import { injectGlobal } from 'emotion';
import { RouterContext } from './router/RouterContext';
import { client } from './graphql/client';
import { colors } from './joykit';
// import { BaseFont } from '@routes/event/guest/services/fonts/base';
import './inter-ui.css';
import { history } from './browserHistory';
import { instantiateTelemetry } from '@services/tracking/telemetry.init';
import { authService } from '@withjoy/web-core';
import { intercomService } from '@services/tracking/intercom.service';
import { TrackingProvider } from '@services/track';
import { AnalyticsProvider, SegmentService, Logger } from '@withjoy/web-core';

import { JoyKitProvider } from '@withjoy/joykit';

if (window.WebFont) {
  window.WebFont.load({
    google: {
      families: ['PT Serif']
    },
    typekit: {
      id: 'wgk3wma'
    }
  });
}

injectGlobal({
  html: {
    fontSize: 16
  },
  'html, body': {
    margin: 0,
    boxSizing: 'border-box',
    minHeight: '100%',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    color: '#333'
  },
  body: {
    fontSize: '1rem',
    color: '#333',
    lineHeight: 1.46,
    '&.dimmed.dimmable': {
      overflow: 'hidden'
    }
  },
  form: {
    marginBottom: 0
  },
  '#root': {
    minHeight: '100%'
  },
  '*, *::after, *::before': {
    boxSizing: 'border-box',
    MozOsxFontSmoothing: 'grayscale',
    WebkitFontSmoothing: 'antialiased',
    fontSmoothing: 'antialiased'
  },

  a: {
    textDecoration: 'none',
    color: colors.action
  },
  p: {
    margin: 0
  },

  '.hidden': {
    display: 'none!important'
  }
});

// injectGlobal({
//   body: {
//     fontFamily: BaseFont.fontFamily
//   },
//   h1: BaseFont.H1,
//   h2: BaseFont.H2,
//   h3: BaseFont.H3,
//   h4: BaseFont.H4,
//   h5: BaseFont.H5,
//   h6: BaseFont.H6,
//   p: BaseFont.p
// });

interface AppProps {
  segment: any;
  logger: any;
}

export const App: React.SFC<AppProps> = ({ segment, logger }) => (
  <Router history={history}>
    <ApolloProvider client={client}>
      <AnalyticsProvider segment={segment} logger={logger}>
        <JoyKitProvider>
          <TrackingProvider>
            <RouterContext />
          </TrackingProvider>
        </JoyKitProvider>
      </AnalyticsProvider>
    </ApolloProvider>
  </Router>
);

export const config = {
  logentriesKey:
    window.joyEnv && window.joyEnv.JOY_CONFIG_LE_TOKEN_BROWSER
      ? window.joyEnv.JOY_CONFIG_LE_TOKEN_BROWSER
      : 'f9f8dd13-c96e-4829-b751-bbef1c6f67c1',
  segmentKey:
    window.joyEnv && window.joyEnv.JOY_CONFIG_SEGMENT_WRITE_KEY
      ? window.joyEnv.JOY_CONFIG_SEGMENT_WRITE_KEY
      : '2xZJ98aly8fmfFDB9msDQlXsyVGVpUby',
  clientId:
    window.joyEnv && window.joyEnv.JOY_CONFIG_AUTH0_CLIENTID
      ? window.joyEnv.JOY_CONFIG_AUTH0_CLIENTID
      : 'EsiknQLsU7Sy4Baqv7cZYM1xzifHQX3r'
};

const getDomain = () => {
  const domain =
    document.domain === 'localhost' || document.domain === 'local.withjoy.com'
      ? `http://${document.domain}:${location.port}`
      : `https://${document.domain}`;
  window.config = () => ({
    host: domain
  });
};

const getApiGateway = (joyEnv: Window['joyEnv']) => {
  if (joyEnv && joyEnv.JOY_CONFIG_BLISS_GATEWAY_HOSTNAME) {
    return `${joyEnv.JOY_CONFIG_BLISS_GATEWAY_PROTOCOL}://${joyEnv.JOY_CONFIG_BLISS_GATEWAY_HOSTNAME}:${
      joyEnv.JOY_CONFIG_BLISS_GATEWAY_PORT
    }`;
  } else {
    return 'https://bliss-gateway.withjoy.com';
  }
};

const bootstrap = async () => {
  const clientId = config.clientId;
  const apiGateway = getApiGateway(window.joyEnv);
  getDomain();
  authService.init(clientId, `${window.config().host}/callback`, apiGateway);
  intercomService.init();

  const segment = new SegmentService(config.segmentKey);
  await segment.init();
  await instantiateTelemetry();

  const logger = new Logger({
    appName: 'Bliss',
    loggentriesToken: config.logentriesKey
  });

  ReactDOM.render(<App segment={segment} logger={logger} />, document.getElementById('react'));
  if (process.env.NODE_ENV === 'production') {
    console.log('service worker enabled');
    serviceWorker.register();
  } else {
    serviceWorker.unregister();
  }
};
bootstrap();
